import { AnyAction } from '@reduxjs/toolkit';
import {
  COMPANY_CHANGED,
  SET_INTENT_UUID,
  TRIGGER_SUBMIT_CONFIGURATION,
  GET_EDIT_CONFIGURATION_ATTEMPT,
  GET_EDIT_CONFIGURATION_SUCCESS,
  GET_EDIT_CONFIGURATION_ERROR,
  GET_EDIT_CONFIGURATION_RESET,
  SET_TEST_COMPANY_MODEL_CONFIGURATION,
  SET_BASE64_METADATA,
  SET_IS_TEST_CONFIGURATION_MODAL_OPEN,
  SET_SELECTED_CONFIGURATION_ANSWERS,
  SET_CLEARED_EXTRA_PRICE_QUESTION_SLUGS,
  SET_TRIGGER_SHOW_SUBMIT_CONFIRMED_MODAL,
  SET_SUBMISSION_IS_SLOW,
  SET_SUBMISSION_BEING_CHECKED,
  SET_FIXED_AMOUNT_CURRENCY,
  SET_INDICATIVE_QUESTION_KEYS,
  TRIGGER_SAVE_TEMPLATE,
  SET_TEMPLATE_TO_APPLY,
  SET_SELECTED_TEMPLATE,
} from '../../constants';
import { ITestCompanyModelConfiguration } from 'types/Configuration.types';
import { ISelectedConfigurationAnswer } from 'types/ConfigurationQuestion.types';
import { ISelectedTemplate } from 'types/Template.types';

export interface ITemplateSaveRequest {
  name: string | null;
}

export interface ISubmissionBeingChecked {
  message: string | null;
}

export interface ISubmitConfigurationReducerState {
  intentUUID: string | null;
  triggerSubmitConfiguration: boolean;
  getEditConfigurationStatus: {
    attempt: boolean;
    success: boolean;
    error: string | null;
  };
  includeBomItems: boolean;
  testCompanyModelConfiguration: ITestCompanyModelConfiguration | null;
  base64Metadata: string | null;
  isTestConfigurationModalOpen: boolean;
  selectedConfigurationAnswers: ISelectedConfigurationAnswer[];
  clearedExtraPriceQuestionSlugs: string[];
  triggerShowSubmitConfirmedModal: boolean;
  // BP-1219: the submission has been on its way for over a minute.
  submissionIsSlow: boolean;
  // BP-1219: its outcome is unknown and being checked; it is not submitted again.
  submissionBeingChecked: ISubmissionBeingChecked | null;
  fixedAmountCurrency: string;
  indicativeQuestionKeys: string[];
  triggerSaveTemplate: ITemplateSaveRequest | null;
  // Id of a template the user picked in the quotation configurator. Non-null
  // means "load this into the form"; QueryProvider consumes it and clears it.
  // Doubles as the in-flight flag that holds off the regular query loop.
  templateToApply: number | null;
  // What the template picker displays. Cleared by "Clear all fields" — the form
  // it describes is gone at that point.
  selectedTemplate: ISelectedTemplate | null;
}

const INIT_STATE: ISubmitConfigurationReducerState = {
  intentUUID: null,
  triggerSubmitConfiguration: false,
  getEditConfigurationStatus: {
    attempt: false,
    success: false,
    error: null,
  },
  includeBomItems: false,
  testCompanyModelConfiguration: null,
  base64Metadata: null,
  isTestConfigurationModalOpen: false,
  selectedConfigurationAnswers: [],
  clearedExtraPriceQuestionSlugs: [],
  triggerShowSubmitConfirmedModal: false,
  submissionIsSlow: false,
  submissionBeingChecked: null,
  fixedAmountCurrency: '',
  indicativeQuestionKeys: [],
  triggerSaveTemplate: null,
  templateToApply: null,
  selectedTemplate: null,
};

const submitConfigurationReducer = (state = INIT_STATE, action: AnyAction) => {
  switch (action.type) {
    case SET_INTENT_UUID:
      return {
        ...state,
        intentUUID: action.payload,
      };
    case TRIGGER_SUBMIT_CONFIGURATION:
      return {
        ...state,
        triggerSubmitConfiguration: action.payload,
      };
    case GET_EDIT_CONFIGURATION_ATTEMPT:
      return {
        ...state,
        getEditConfigurationStatus: {
          attempt: true,
          success: false,
          error: null,
        },
      };
    case GET_EDIT_CONFIGURATION_SUCCESS:
      return {
        ...state,
        getEditConfigurationStatus: {
          attempt: false,
          success: true,
          error: null,
        },
      };
    case GET_EDIT_CONFIGURATION_ERROR:
      return {
        ...state,
        getEditConfigurationStatus: {
          attempt: false,
          success: false,
          error: action.payload,
        },
      };
    case GET_EDIT_CONFIGURATION_RESET:
      return {
        ...state,
        getEditConfigurationStatus: {
          attempt: false,
          success: false,
          error: null,
        },
      };
    case SET_TEST_COMPANY_MODEL_CONFIGURATION:
      return {
        ...state,
        testCompanyModelConfiguration: action.payload,
      };
    case SET_BASE64_METADATA:
      return {
        ...state,
        base64Metadata: action.payload,
      };
    case SET_IS_TEST_CONFIGURATION_MODAL_OPEN:
      return {
        ...state,
        isTestConfigurationModalOpen: action.payload,
      };
    case SET_SELECTED_CONFIGURATION_ANSWERS:
      return {
        ...state,
        selectedConfigurationAnswers: action.payload,
      };
    case SET_CLEARED_EXTRA_PRICE_QUESTION_SLUGS:
      return {
        ...state,
        clearedExtraPriceQuestionSlugs: action.payload,
      };
    case SET_TRIGGER_SHOW_SUBMIT_CONFIRMED_MODAL:
      return {
        ...state,
        triggerShowSubmitConfirmedModal: action.payload,
      };
    case SET_SUBMISSION_IS_SLOW:
      return {
        ...state,
        submissionIsSlow: action.payload,
      };
    case SET_SUBMISSION_BEING_CHECKED:
      return {
        ...state,
        submissionBeingChecked: action.payload,
      };
    case SET_FIXED_AMOUNT_CURRENCY:
      return {
        ...state,
        fixedAmountCurrency: action.payload,
      };
    case COMPANY_CHANGED:
      return {
        ...INIT_STATE,
      };
    case SET_INDICATIVE_QUESTION_KEYS:
      return {
        ...state,
        indicativeQuestionKeys: action.payload,
      };
    case TRIGGER_SAVE_TEMPLATE:
      return {
        ...state,
        triggerSaveTemplate: action.payload,
      };
    case SET_TEMPLATE_TO_APPLY:
      return {
        ...state,
        templateToApply: action.payload,
      };
    case SET_SELECTED_TEMPLATE:
      return {
        ...state,
        selectedTemplate: action.payload,
      };
    default:
      return state;
  }
};

export default submitConfigurationReducer;
